import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer } from "lucide-react";
import LabelTile from "@/components/label/LabelTile";
import { loadLabelFormat, resolveFormat } from "@/lib/labelFormat";

/**
 * Single-carton label print.
 *
 * Honours the format saved on the Custom Labels page (label size, font
 * family/size, and which fields to print). Used both for "Print label" from
 * the UI and for Quick Weigh's auto-print hidden iframe.
 *
 * URL: /label/:box_no?autoprint=1
 */
export default function CartonLabel() {
  const { box_no } = useParams();
  const [search] = useSearchParams();
  const autoPrint = search.get("autoprint") === "1";
  const [data, setData] = useState(null);
  const printedRef = React.useRef(false);

  const fmt = useMemo(() => resolveFormat(loadLabelFormat()), []);
  const { preset, family, scale, fieldSet, customText, fieldSizes } = fmt;

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/cartons/${box_no}/activity`);
        setData(r.data);
      } catch {
        setData({ error: true });
      }
    })();
  }, [box_no]);

  useEffect(() => {
    if (!autoPrint || !data || data.error || printedRef.current) return;
    printedRef.current = true;
    const t = setTimeout(() => {
      try { window.focus(); } catch (_e) { /* no-op */ }
      window.print();
    }, 400);
    return () => clearTimeout(t);
  }, [autoPrint, data]);

  if (!data) return <div className="p-8 font-mono text-xs text-zinc-500">Loading label…</div>;
  if (data.error) return <div className="p-8 font-mono text-xs text-red-700">Carton {box_no} not found.</div>;

  const perPage = preset.perPage;
  // For the fixed single-carton view we duplicate to fill a 2-up sheet, but
  // render a single tile for any other perPage.
  const tiles = perPage === 2 ? [data, data] : [data];

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white p-4 print:p-0 flex justify-center" data-testid="carton-label-page">
      <div className="w-full max-w-[520px] flex flex-col items-center gap-3">
        <div className="w-full flex items-center justify-between print:hidden text-xs">
          <a href="/inventory" className="font-mono text-zinc-500 hover:text-orange-700">← Inventory</a>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-[11px] font-mono uppercase tracking-wider"
            data-testid="print-label-btn"
          >
            <Printer size={12} /> Print label
          </button>
        </div>

        <div
          data-pair-card
          data-testid="carton-label-pair"
          className="bg-zinc-100 print:bg-white flex flex-row"
          style={{ width: `${preset.pageW}mm`, height: `${preset.pageH}mm`, gap: perPage === 2 ? "4mm" : "0" }}
        >
          {tiles.map((d, i) => (
            <LabelTile key={i} d={d} preset={preset}
              fontFamily={family.css} fontScale={scale.mult}
              fieldSet={fieldSet} customText={customText}
              fieldSizes={fieldSizes} />
          ))}
        </div>

        <div className="text-[10px] font-mono text-zinc-500 print:hidden">
          Format: <span className="text-zinc-800 font-semibold">{preset.label}</span> — change it on the{" "}
          <a href="/labels/custom" className="text-orange-700 hover:underline">Custom Labels</a> page.
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: ${preset.pageW}mm ${preset.pageH}mm;
            margin: 0;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          [data-testid="carton-label-page"] { padding: 0 !important; }
          [data-testid="carton-label-page"] > div { max-width: none !important; gap: 0 !important; }
          [data-testid="carton-label-page"] .print\\:hidden { display: none !important; }
          [data-pair-card] {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            justify-content: flex-start !important;
            width: ${preset.pageW}mm !important;
            height: ${preset.pageH}mm !important;
            gap: ${perPage === 2 ? "4mm" : "0"} !important;
            margin: 0 !important;
            padding: 0 !important;
            page-break-after: auto;
            page-break-inside: avoid;
            break-inside: avoid;
            background: white !important;
          }
          [data-label-card] {
            box-sizing: border-box !important;
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}
